/* eslint-disable react/display-name */
import {
  FormControlLabel,
  List,
  ListItem,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as Cesium from "cesium";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "~/redux/store/store";
import { LayerTitle } from "../../types/layers";
import { getCesiumLayers } from "../functions/getCesiumLayers";
import { updateCesiumLayer } from "../functions/updateCesiumLayer";
import { CesiumMapLayer } from "../types/CesiumMapItems";

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.primary.contrastText,
  },
}));

interface Props {
  viewer: Cesium.Viewer;
}

export type LayerExtraSettings = {
  title: LayerTitle;
  content: JSX.Element;
};

export type CesiumLayer = {
  title: CesiumMapLayer;
  visible: boolean;
};

export const CesiumLayerSettings = React.forwardRef<HTMLUListElement, Props>(
  ({ viewer }: Props, ref): JSX.Element => {
    const classes = useStyles();
    const { t } = useTranslation("cesium");
    const [layers, setLayers] = React.useState<CesiumLayer[]>([]);
    const [changed, setChanged] = React.useState(false);

    const primitivesChanged = useAppSelector(
      (s) => s.map.cesiumPrimitivesChanged,
    );

    const handleVisibilityToggle = (l: CesiumLayer) => {
      updateCesiumLayer({ ...l, visible: !l.visible }, viewer);
      setChanged((prev) => !prev);
    };

    // Check the map for entities/layers
    React.useEffect(() => {
      const listener = () => {
        setLayers(getCesiumLayers(viewer));
      };
      const removeListener =
        viewer.entities.collectionChanged.addEventListener(listener);
      const dataSourceListenerRemoveCb =
        viewer.dataSources.dataSourceAdded.addEventListener(listener);
      listener();
      return () => {
        removeListener();
        dataSourceListenerRemoveCb();
      };
    }, [viewer.entities, primitivesChanged, viewer, changed]);

    if (!layers?.length) {
      return (
        <Typography
          variant="h6"
          align="center"
          sx={{ color: (theme) => theme.palette.primary.contrastText }}
          ref={ref}
        >
          {t("no-layers")}
        </Typography>
      );
    }

    return (
      <List ref={ref} sx={{ height: "100%", overflow: "auto" }}>
        <ListItem>
          <Typography variant="h4" className={classes.title}>
            {t("map:layer-settings-title")}
          </Typography>
        </ListItem>
        <Paper
          sx={{
            margin: (theme) => theme.spacing(1, 2),
            backgroundColor: (theme) => theme.palette.primary.semiLight,
            color: (theme) => theme.palette.primary.contrastText,
          }}
        >
          <List sx={{ width: "100%" }}>
            {layers.map((l) => (
              <ListItem key={l.title}>
                <FormControlLabel
                  label={t(l.title)}
                  control={<Switch checked={l.visible} color="secondary" />}
                  onChange={() => handleVisibilityToggle(l)}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </List>
    );
  },
);
